import React, { useEffect, useRef, useState } from "react";
import "./styles/GrassField.css";
import grass1 from "../assets/grass1.png";
import grass2 from "../assets/grass2.png";
import grass3 from "../assets/grass3.png";
import grass4 from "../assets/grass4.png";

const grassAssets = [grass1, grass2, grass3, grass4];

export default function GrassField() {
  const containerRef = useRef(null);
  const bladeRefs = useRef([]);
  const metaRef = useRef([]);
  const tiltRef = useRef([]);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [bladeCount, setBladeCount] = useState(getBladeCount());

  function getBladeCount() {
    return window.innerWidth < 600 ? 60 : 80;
  }


  useEffect(() => {
    metaRef.current = Array.from({ length: bladeCount }, () => {
      const scale = 0.75 + Math.random() * 0.7;
      const zIndex = Math.random() < 0.35 ? 0 : 1;
      const jitter = (Math.random() - 0.5) * 1.4;
      return { scale, zIndex, jitter };
    });
    tiltRef.current = new Array(bladeCount).fill(0);

    const base = 120;
    const maxScale = metaRef.current.reduce((m, b) => Math.max(m, b.scale), 1);
    const pad = 16;
    const needed = Math.round(base * maxScale + pad);
    containerRef.current?.style.setProperty("--grass-height", `${needed}px`);
  }, [bladeCount]);

  useEffect(() => {
    const onMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    const onResize = () => setBladeCount(getBladeCount());
    window.addEventListener("mousemove", onMove);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    let raf;
    const swayOffsets = Array.from({ length: bladeCount }).map(() => ({
      delay: Math.random() * 1000,
      speed: 500 + Math.random() * 1000,
    }));

    const tick = () => {
      const time = Date.now();
      const cRect = containerRef.current?.getBoundingClientRect();

      const inside =
        !!cRect &&
        mousePos.x >= cRect.left &&
        mousePos.x <= cRect.right &&
        mousePos.y >= cRect.top &&
        mousePos.y <= cRect.bottom;

      bladeRefs.current.forEach((blade, i) => {
        if (!blade) return;

        const idle =
          Math.sin((time + swayOffsets[i].delay) / swayOffsets[i].speed) * 2;
        let target = idle;

        if (inside) {
          const r = blade.getBoundingClientRect();
          const over =
            mousePos.x >= r.left &&
            mousePos.x <= r.right &&
            mousePos.y >= r.top &&
            mousePos.y <= r.bottom;

          if (over) {
            const hover = ((mousePos.x - r.left) / r.width - 0.5) * 30;
            target = idle + hover;
          }
        }

        const prev = tiltRef.current[i] || 0;
        const smoothed = prev + (target - prev) * 0.15;
        tiltRef.current[i] = smoothed;

        blade.style.transform = `translateX(-50%) rotate(${smoothed}deg)`;
      });

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mousePos, bladeCount]);

  return (
    <div className="grass-container" style={{ height: "100%" }}>
      <div ref={containerRef} className="grass-field">
        {Array.from({ length: bladeCount }).map((_, i) => {
          const meta = metaRef.current[i] || {};
          return (
            <img
              key={i}
              ref={(el) => (bladeRefs.current[i] = el)}
              src={grassAssets[i % grassAssets.length]}
              className="grass-blade"
              alt="blade"
              style={{
                left: `calc(${(i / bladeCount) * 100}% + ${meta.jitter || 0}vw)`,
                height: `${(meta.scale || 1) * 100}%`,
                bottom: `0px`,
                zIndex: meta.zIndex ?? 1,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
